/**
 * Shared Due Diligence Types
 *
 * Types for collaborative DD between investors.
 * Premium feature: share DD with other investors for collaborative analysis.
 */

// ============================================================================
// Core Types
// ============================================================================

export interface SharedDueDiligence {
  id: string;
  startupId: string;
  startupName: string;
  ownerId: string; // Premium investor who created the share
  ownerName: string;
  createdAt: string;
  updatedAt: string;
  collaborators: DDCollaborator[];
  notes: DDNote[];
  status: 'active' | 'closed';
  syndicateMode: boolean; // If true, collaborators can see each other's conclusions
}

export interface DDCollaborator {
  id: string;
  odId: string; // SharedDueDiligence ID
  investorId: string;
  investorName: string;
  investorEmail: string;
  role: CollaboratorRole;
  status: CollaboratorStatus;
  invitedAt: string;
  joinedAt?: string;
  conclusion?: DDConclusion;
  lastActiveAt?: string;
}

export type CollaboratorRole =
  | 'owner'      // Created the shared DD, full control
  | 'collaborator' // Can add notes and conclusions
  | 'observer'   // Can view and add notes, but no conclusions
  | 'viewer';    // Can only view, no notes

export type CollaboratorStatus =
  | 'pending'    // Invited but not accepted
  | 'active'     // Actively participating
  | 'declined'   // Declined the invite
  | 'removed';   // Removed by owner

export interface DDNote {
  id: string;
  sharedDdId: string;
  authorId: string;
  authorName: string;
  content: string;
  category: NoteCategory;
  createdAt: string;
  updatedAt?: string;
  isPrivate: boolean; // If true, only visible to author
  reactions?: NoteReaction[];
  documentRef?: DDDocumentRef; // Optional link to a specific document
}

/**
 * Reference to a data room document
 */
export interface DDDocumentRef {
  documentId: string;
  documentName: string;
  documentType?: string; // e.g., 'pitch_deck', 'financials', etc.
}

export type NoteCategory =
  | 'general'
  | 'strength'
  | 'concern'
  | 'question'
  | 'action_item';

export interface NoteReaction {
  investorId: string;
  type: 'agree' | 'disagree' | 'important';
}

export interface DDConclusion {
  investorId: string;
  decision: ConclusionDecision;
  investmentAmount?: number; // In CHF, if committing
  notes?: string;
  updatedAt: string;
}

export type ConclusionDecision =
  | 'pass'        // Not interested
  | 'watching'    // Interested but not ready
  | 'interested'  // Actively interested
  | 'committed';  // Ready to invest (syndicate mode)

export interface DDInvite {
  id: string;
  sharedDdId: string;
  email: string;
  role: CollaboratorRole;
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Invite expiry duration in days
 */
export const DD_INVITE_EXPIRY_DAYS = 14;

/**
 * Maximum collaborators per shared DD
 */
export const MAX_COLLABORATORS = 10;

/**
 * Note category display config
 */
export const NOTE_CATEGORIES: Record<NoteCategory, { label: string; color: string }> = {
  general: { label: 'General', color: 'gray' },
  strength: { label: 'Strength', color: 'green' },
  concern: { label: 'Concern', color: 'red' },
  question: { label: 'Question', color: 'blue' },
  action_item: { label: 'Action Item', color: 'orange' },
};

/**
 * Conclusion decision display config
 */
export const CONCLUSION_CONFIG: Record<ConclusionDecision, { label: string; color: string; emoji: string }> = {
  pass: { label: 'Pass', color: 'gray', emoji: '👎' },
  watching: { label: 'Watching', color: 'blue', emoji: '👀' },
  interested: { label: 'Interested', color: 'green', emoji: '👍' },
  committed: { label: 'Committed', color: 'purple', emoji: '🤝' },
};

/**
 * Role display config
 */
export const ROLE_CONFIG: Record<CollaboratorRole, { label: string; canNote: boolean; canConclude: boolean }> = {
  owner: { label: 'Owner', canNote: true, canConclude: true },
  collaborator: { label: 'Collaborator', canNote: true, canConclude: true },
  observer: { label: 'Observer', canNote: true, canConclude: false },
  viewer: { label: 'Viewer', canNote: false, canConclude: false },
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a unique invite token
 */
export function generateDDInviteToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Check if an invite has expired
 */
export function isDDInviteExpired(invite: DDInvite): boolean {
  return new Date() > new Date(invite.expiresAt);
}

/**
 * Check if a user can add notes
 */
export function canAddNotes(role: CollaboratorRole): boolean {
  return ROLE_CONFIG[role].canNote;
}

/**
 * Check if a user can set conclusions
 */
export function canSetConclusion(role: CollaboratorRole): boolean {
  return ROLE_CONFIG[role].canConclude;
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Get syndicate summary
 */
export function getSyndicateSummary(collaborators: DDCollaborator[]): {
  total: number;
  passed: number;
  watching: number;
  interested: number;
  committed: number;
  totalCommitted: number; // Total investment amount
} {
  const active = collaborators.filter((c) => c.status === 'active');
  return {
    total: active.length,
    passed: active.filter((c) => c.conclusion?.decision === 'pass').length,
    watching: active.filter((c) => c.conclusion?.decision === 'watching').length,
    interested: active.filter((c) => c.conclusion?.decision === 'interested').length,
    committed: active.filter((c) => c.conclusion?.decision === 'committed').length,
    totalCommitted: active
      .filter((c) => c.conclusion?.decision === 'committed')
      .reduce((sum, c) => sum + (c.conclusion?.investmentAmount || 0), 0),
  };
}
